import { create } from "zustand";
import type {
	ApiFormConfig,
	ApiPage,
	ApiQuestion,
	Brandkit,
} from "../types/api.types";
import { feedbackApi } from "../services/api.service";
import type { FormBrand } from "../types/api.types";

// Page types for positive flow (>= threshold stars)
const POSITIVE_FLOW_PAGE_TYPES = [
	"rating",
	"welcome",
	"about_user",
	"about_company",
	"consent",
	"thank_you",
];

// Page types for negative flow (< threshold stars)
const NEGATIVE_FLOW_PAGE_TYPES = ["rating", "private_feedback", "negative_thank_you"];

interface FormConfigState {
	// State
	formSlug: string | null;
	brand: FormBrand | null;
	brandkit: Brandkit | null;
	config: ApiFormConfig | null;
	isLoading: boolean;
	error: string | null;

	// Actions
	loadConfig: (formSlug: string) => Promise<void>;
	loadBrand: (formSlug: string) => Promise<FormBrand>;
	loadBrandkit: (formSlug: string) => Promise<Brandkit>;
	reset: () => void;

	// Computed getters
	getPages: () => ApiPage[];
	getQuestions: () => ApiQuestion[];
	getRatingThreshold: () => number;
	getPositiveFlowPages: () => ApiPage[];
	getNegativeFlowPages: () => ApiPage[];
	getIsDarkMode: () => boolean;
}

export const useFormConfigStore = create<FormConfigState>((set, get) => ({
	formSlug: null,
	brand: null,
	brandkit: null,
	config: null,
	isLoading: false,
	error: null,

	loadConfig: async (formSlug: string) => {
		set({ isLoading: true, error: null });
		try {
			const response = await feedbackApi.getFormConfig(formSlug);
			set({
				formSlug,
				config: response.data,
				isLoading: false,
				error: null,
			});
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to load form";
			set({
				isLoading: false,
				error: message,
			});
			throw error;
		}
	},

	loadBrand: async (formSlug: string) => {
		set({ isLoading: true, error: null });
		try {
			const response = await feedbackApi.getFormBrand(formSlug);
			set({
				formSlug,
				brand: response.data,
				isLoading: false,
				error: null,
			});
			return response.data;
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to load form";
			set({
				isLoading: false,
				error: message,
			});
			throw error;
		}
	},

	loadBrandkit: async (brandSlug: string) => {
		set({ isLoading: true, error: null });
		try {
			const response = await feedbackApi.getBrandkit(brandSlug);
			set({
				brandkit: response.data,
				isLoading: false,
				error: null,
			});
			return response.data;
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Failed to load form";
			set({
				isLoading: false,
				error: message,
			});
			throw error;
		}
	},

	reset: () => {
		set({
			formSlug: null,
			config: null,
			isLoading: false,
			error: null,
		});
	},

	getPages: () => {
		const config = get().config;
		if (!config) return [];
		return [...config.pages].sort((a, b) => a.sort_order - b.sort_order);
	},

	getQuestions: () => {
		const config = get().config;
		if (!config) return [];
		return [...config.questions].sort(
			(a, b) => a.sort_order - b.sort_order,
		);
	},

	getRatingThreshold: () => {
		return get().config?.rating_positive_threshold ?? 4;
	},

	getPositiveFlowPages: () => {
		const pages = get().getPages();
		return pages.filter(
			(p) => POSITIVE_FLOW_PAGE_TYPES.includes(p.type) && (p.status ?? "enabled") !== "disabled"
		);
	},

	getNegativeFlowPages: () => {
		const pages = get().getPages();
		return pages.filter(
			(p) => NEGATIVE_FLOW_PAGE_TYPES.includes(p.type) && (p.status ?? "enabled") !== "disabled"
		);
	},

	getIsDarkMode: () => {
		const theme = get().config?.theme;
		if (theme === "dark") return true;
		if (theme === "light") return false;
		// system-default: use system preference
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	},
}));
